import { useRef, useState } from 'react';
import '../styles/Contact.css';
import { getEmailJsTemplateId, sendEmailJsForm } from '../utils/emailjsClient';
import ReCAPTCHA from 'react-google-recaptcha';
import { getRecaptchaSiteKey } from '../utils/recaptcha';
import { useRecaptchaGate } from '../hooks/useRecaptchaGate';

const content = {
  swe: {
    heroTitle: 'Kontakta Execora',
    heroText:
      'Berätta vad du behöver hjälp med. Vi återkommer snabbt med rätt nästa steg.',
    cardTitle: 'Skicka förfrågan',
    cardText:
      'Fyll i formuläret nedan. Fält markerade med * är obligatoriska.',
    labels: {
      name: 'Namn *',
      company: 'Företag *',
      email: 'E-post *',
      phone: 'Telefon *',
      subject: 'Ämne *',
      message: 'Meddelande *'
    },
    placeholders: {
      name: 'För- och efternamn',
      company: 'Ditt företag',
      email: 'namn@företag.se',
      phone: '070-123 45 67',
      message: 'Beskriv kort ert behov, tidsram och vilken kompetens ni söker.'
    },
    subjects: [
      { value: '', label: 'Välj ämne' },
      { value: 'hiring-consultants', label: 'Bemanning: IT, teknik och ingenjörer' },
      { value: 'finance-consultants', label: 'Bemanning: ekonomer och finans' },
      { value: 'recruitment', label: 'Rekrytering av specialistroll' },
      { value: 'interim', label: 'Interimlösning' },
      { value: 'job-seeker', label: 'Jobbsökande' },
      { value: 'partnership', label: 'Samarbete/partnerskap' },
      { value: 'other', label: 'Övrigt' }
    ],
    submit: 'Skicka meddelande',
    success: 'Tack! Din förfrågan är skickad. Vi återkommer så snart som möjligt.',
    privacy: 'Genom att skicka formuläret godkänner du att vi behandlar dina uppgifter för att kontakta dig.',
    recaptchaHint: 'Fyll i alla obligatoriska fält ovan för att kunna använda reCAPTCHA.'
  },
  eng: {
    heroTitle: 'Contact Execora',
    heroText:
      'Tell us what support you need. We will follow up quickly with clear next steps.',
    cardTitle: 'Send request',
    cardText:
      'Complete the form below. Fields marked with * are required.',
    labels: {
      name: 'Full name *',
      company: 'Company *',
      email: 'Email *',
      phone: 'Phone *',
      subject: 'Subject *',
      message: 'Message *'
    },
    placeholders: {
      name: 'First and last name',
      company: 'Your company',
      email: 'name@company.com',
      phone: '+46 70 123 45 67',
      message: 'Briefly describe your need, timeline, and what competence you are looking for.'
    },
    subjects: [
      { value: '', label: 'Select subject' },
      { value: 'hiring-consultants', label: 'Staffing: IT, technology and engineers' },
      { value: 'finance-consultants', label: 'Staffing: finance professionals' },
      { value: 'recruitment', label: 'Recruitment for specialist role' },
      { value: 'interim', label: 'Interim solution' },
      { value: 'job-seeker', label: 'Job seeker' },
      { value: 'partnership', label: 'Partnership' },
      { value: 'other', label: 'Other' }
    ],
    submit: 'Send message',
    success: 'Thank you! Your request has been sent. We will get back to you shortly.',
    privacy: 'By sending this form, you consent to us processing your data to contact you.',
    recaptchaHint: 'Complete all required fields above to use reCAPTCHA.'
  }
};

function Contact({ lang = 'swe' }) {
  const copy = content[lang] || content.swe;
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const formRef = useRef(null);
  const captchaRef = useRef(null);
  const [recaptchaAllowed, syncRecaptchaGate] = useRecaptchaGate(formRef, captchaRef, setCaptchaToken);
  const captchaExpiredMessage = lang === 'swe'
    ? 'Verifieringen gick ut. Bekräfta att du inte är en robot igen.'
    : 'Verification expired. Please confirm you are not a robot again.';
  const captchaErrorMessage = lang === 'swe'
    ? 'reCAPTCHA kunde inte laddas klart. Ladda om sidan och försök igen.'
    : 'reCAPTCHA could not finish loading. Refresh the page and try again.';

  const handleCaptchaExpired = () => {
    setCaptchaToken('');
    if (status === 'sending') {
      setStatus('idle');
    }
    setError(captchaExpiredMessage);
  };

  const handleCaptchaErrored = () => {
    setCaptchaToken('');
    if (status === 'sending') {
      setStatus('idle');
    }
    setError(captchaErrorMessage);
    captchaRef.current?.reset?.();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    const formEl = formRef.current;
    if (!formEl) {
      return;
    }
    if (!formEl.checkValidity()) {
      formEl.reportValidity();
      return;
    }

    setStatus('sending');

    try {
      const siteKey = getRecaptchaSiteKey();
      const token = captchaRef.current?.getValue?.() || captchaToken;
      if (siteKey && !token) {
        setStatus('idle');
        setError(lang === 'swe' ? 'Bekräfta att du inte är en robot.' : 'Please confirm you are not a robot.');
        return;
      }

      const timeEl = formEl.querySelector('input[name="time"]');
      if (timeEl) {
        timeEl.value = new Date().toLocaleString(lang === 'swe' ? 'sv-SE' : 'en-GB', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      }

      const subjectEl = formEl.querySelector('select[name="subject"]');
      const messageEl = formEl.querySelector('textarea[name="message"]');
      const detailsEl = formEl.querySelector('input[name="details"]');
      if (detailsEl) {
        const subjectLabel = subjectEl?.selectedOptions?.[0]?.textContent?.trim() || '';
        const message = messageEl?.value?.trim() || '';
        const lines = [];
        if (subjectLabel) lines.push(`${lang === 'swe' ? 'Ämne' : 'Subject'}: ${subjectLabel}`);
        if (message) {
          lines.push('');
          lines.push(message);
        }
        detailsEl.value = lines.join('\n');
      }

      await sendEmailJsForm(formEl, getEmailJsTemplateId('contact'));
      setStatus('sent');
      formEl.reset();
      syncRecaptchaGate();
    } catch (err) {
      // Helpful during setup (EmailJS errors show up here)
      // eslint-disable-next-line no-console
      console.error('EmailJS send failed (contact)', err);
      setStatus('idle');
      setError(lang === 'swe' ? 'Kunde inte skicka just nu. Försök igen.' : 'Could not send right now. Please try again.');
    }
  };

  return (
    <section className="contact-page">
      <div className="contact-hero">
        <p className="contact-eyebrow">Execora Consulting</p>
        <h1>{copy.heroTitle}</h1>
        <p>{copy.heroText}</p>
      </div>

      <div className="contact-card">
        <div className="contact-card-header">
          <h2>{copy.cardTitle}</h2>
          <p>{copy.cardText}</p>
        </div>

        <form
          ref={formRef}
          className="contact-form"
          onSubmit={handleSubmit}
          onInput={syncRecaptchaGate}
          onChange={syncRecaptchaGate}
        >
          <input type="hidden" name="source" value="contact" />
          <input type="hidden" name="time" value="" />
          <input type="hidden" name="context_title" value={lang === 'swe' ? 'Kontaktformulär' : 'Contact form'} />
          <input type="hidden" name="details" value="" />
          <div className="contact-grid">
            <label>
              <span>{copy.labels.name}</span>
              <input type="text" name="name" placeholder={copy.placeholders.name} required />
            </label>
            <label>
              <span>{copy.labels.company}</span>
              <input type="text" name="company" placeholder={copy.placeholders.company} required />
            </label>
            <label>
              <span>{copy.labels.email}</span>
              <input type="email" name="email" placeholder={copy.placeholders.email} required />
            </label>
            <label>
              <span>{copy.labels.phone}</span>
              <input
                type="tel"
                name="phone"
                placeholder={copy.placeholders.phone}
                required
              />
            </label>
          </div>

          <label className="contact-subject">
            <span>{copy.labels.subject}</span>
            <select name="subject" defaultValue="" required>
              {copy.subjects.map((subject) => (
                <option key={subject.value || 'empty'} value={subject.value} disabled={subject.value === ''}>
                  {subject.label}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>{copy.labels.message}</span>
            <textarea name="message" rows="6" placeholder={copy.placeholders.message} required />
          </label>

          {getRecaptchaSiteKey() && (
            <>
              <div
                className={`recaptcha-wrap${recaptchaAllowed ? '' : ' recaptcha-wrap--gated'}`}
                aria-disabled={!recaptchaAllowed}
              >
                <ReCAPTCHA
                  ref={captchaRef}
                  sitekey={getRecaptchaSiteKey()}
                  onChange={(token) => setCaptchaToken(token || '')}
                  onExpired={handleCaptchaExpired}
                  onErrored={handleCaptchaErrored}
                  hl={lang === 'swe' ? 'sv' : 'en'}
                />
              </div>
              {!recaptchaAllowed && (
                <p className="recaptcha-hint" role="status">
                  {copy.recaptchaHint}
                </p>
              )}
            </>
          )}

          <div className="contact-actions">
            <button type="submit" className="contact-submit" disabled={status === 'sending'}>
              {status === 'sending' ? (lang === 'swe' ? 'Skickar…' : 'Sending…') : copy.submit}
            </button>
            <p className="contact-privacy">{copy.privacy}</p>
          </div>
        </form>

        {error && <p className="contact-success">{error}</p>}
        {status === 'sent' && !error && <p className="contact-success">{copy.success}</p>}
      </div>
    </section>
  );
}

export default Contact;
